/**
 * The help assistant's anchor catalogue (D10-ACT-004, #3606).
 *
 * The controls the assistant may point at with `highlight(anchor, text)`, taken
 * from the guided-tour step definitions -- each step already names its route, its
 * `data-tour` anchor and a title, so `{anchor, route, label}` comes for free and
 * stays in step with the tours' own tests. One builder serves both sides:
 * `SupportKbCatalog.test.ts` exports it into the backend knowledge pack (the
 * server validates against it), and the panel re-validates a streamed highlight
 * against it before anything is shown.
 *
 * Left out, because a bare highlight could never find them:
 *  - `center` steps (no anchor at all);
 *  - record pages (`:param` routes -- there is no id to open);
 *  - sidebar anchors (`nav-*`, and any step that expands a nav group): hidden in
 *    a collapsed accordion or the mobile drawer;
 *  - modal-only anchors (the target of an `openVia` step mounts only inside the
 *    modal the tour opens). The opener itself is a plain button on the page, so
 *    it IS kept, labelled with the step's title ("Add a new contact").
 * Every registered tour is included: they all run inside the CRM shells
 * (the client-portal tour is the staff Portal panel on a deal, #3624).
 */
import { TOUR_REGISTRY } from "../tours/registry";
import { HELP_ANCHOR_RE, TOUR_ANCHOR_RE, isHelpAnchor } from "../tours/anchorSelector";
import { routeMatchesStep } from "../tours/routeMatch";
import { HELP_ANCHORS, RECORD_HELP_ANCHORS } from "./helpAnchors.generated";

export interface SupportAnchor {
  /** The `data-tour` value on the page. */
  anchor: string;
  /** The static route the anchor lives on. */
  route: string;
  /** The tour step's title (may carry the `{agency}` token). */
  label: string;
}

/**
 * Same id shapes the server accepts for a highlight: a tour anchor
 * (`sanitize.ID_RE`) or a `data-help` anchor, `<page-key>.<control>` (#3630).
 */
export const ANCHOR_ID_RE = new RegExp(`${TOUR_ANCHOR_RE.source}|${HELP_ANCHOR_RE.source}`);

function usable(anchor: string, route: string): boolean {
  return (
    anchor !== "center" &&
    TOUR_ANCHOR_RE.test(anchor) &&
    !anchor.startsWith("nav-") &&
    route.startsWith("/") &&
    !route.includes(":")
  );
}

/**
 * Every `{anchor, route}` pair the assistant may highlight, deterministic order
 * (tour id, then step order), first label wins for a repeated pair.
 */
export function buildAnchorCatalog(): SupportAnchor[] {
  const out: SupportAnchor[] = [];
  const seen = new Set<string>();
  const add = (anchor: string, route: string, label: string) => {
    if (!usable(anchor, route)) return;
    const key = `${anchor} ${route}`;
    if (seen.has(key)) return;
    seen.add(key);
    out.push({ anchor, route, label });
  };
  const tours = Object.values(TOUR_REGISTRY).sort((a, b) => a.id.localeCompare(b.id));
  for (const tour of tours) {
    for (const step of tour.steps) {
      if (step.navGroup) continue;
      if (step.openVia) {
        add(step.openVia, step.route, step.title);
        continue;
      }
      add(step.target, step.route, step.title);
    }
  }
  return out;
}

let cached: SupportAnchor[] | null = null;
let helpPairs: Set<string> | null = null;

/**
 * True when `{anchor, route}` may be highlighted.
 *
 * A tour anchor must be in the tour catalogue above. A `data-help` anchor
 * (`<page-key>.<control>`, #3630) must be one the page guides document for
 * that route: `helpAnchors.generated.ts`, written by `build_support_kb` from the
 * same knowledge pack the server validates against, so both sides check.
 */
export function isCatalogAnchor(anchor: string, route: string): boolean {
  if (isHelpAnchor(anchor)) {
    helpPairs ??= new Set(HELP_ANCHORS.map(([a, r]) => `${a} ${r}`));
    return helpPairs.has(`${anchor} ${route}`);
  }
  cached ??= buildAnchorCatalog();
  return cached.some((a) => a.anchor === anchor && a.route === route);
}

/**
 * True when `anchor` may be highlighted on the record page at `route` (#3633).
 *
 * `route` must be a concrete record path (`/crm/deals/42`, numeric ids only,
 * nothing but plain path segments) that is an instance of a record pattern the
 * page guides document the anchor on, and the page that grants access to those
 * records must be one of the user's allowed pages. Callers also require `route`
 * to be the page the browser is showing, so a record highlight never navigates.
 */
export function isRecordCatalogAnchor(
  anchor: string,
  route: string,
  allowedRoutes: readonly string[],
): boolean {
  if (!isHelpAnchor(anchor) || !/^(\/[a-z0-9][a-z0-9-]*)+$/.test(route)) return false;
  return RECORD_HELP_ANCHORS.some(
    ([a, pattern, page]) =>
      a === anchor &&
      allowedRoutes.includes(page) &&
      routeMatchesStep(route, pattern, { numericParams: true }),
  );
}
